import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComparisonHero from "./components/ComparisonHero";
import MasterComparisonTable from "./components/MasterComparisonTable";
import SystemDeepDives from "./components/SystemDeepDives";
import CostBreakdown from "./components/CostBreakdown";
import UseCaseSelector from "./components/UseCaseSelector";
import ComparisonFAQ from "./components/ComparisonFAQ";
import ComparisonCTA from "./components/ComparisonCTA";

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Solar vs Gas vs Whole Home Backup Power: Full Comparison Guide",
  description:
    "Compare solar generators, gas generators, and whole home standby systems side-by-side. Unbiased cost breakdowns, pros and cons, and use-case recommendations for homeowners in 2024.",
  url: "https://learn.ironhavensupply.com/compare-backup-power-systems",
  publisher: {
    "@type": "Organization",
    name: "Iron Haven Supply",
    url: "https://ironhavensupply.com",
    telephone: "+18666054975",
  },
  mainEntity: {
    "@type": "Article",
    headline: "Solar Generator vs Gas Generator vs Whole Home Standby — The Complete 2024 Comparison",
    author: { "@type": "Organization", name: "Iron Haven Supply" },
    datePublished: "2024-01-01",
    dateModified: "2024-03-31",
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://learn.ironhavensupply.com" },
    { "@type": "ListItem", position: 2, name: "Compare Backup Power Systems", item: "https://learn.ironhavensupply.com/compare-backup-power-systems" },
  ],
};

const brands: Array<{ name: string; claim: string; icon: string; href: string }> = [
  {
    name: "EcoFlow",
    claim: "Industry leader in portable power stations with expandable battery ecosystems.",
    icon: "ri-flashlight-line",
    href: "https://ironhavensupply.com/collections/solar-generators",
  },
  {
    name: "Nature&apos;s Generator",
    claim: "Whole-home capable solar power systems designed to replace traditional standby generators.",
    icon: "ri-sun-foggy-line",
    href: "https://ironhavensupply.com/collections/solar-generators",
  },
  {
    name: "Renogy",
    claim: "Trusted name in solar panels, charge controllers, and off-grid energy components.",
    icon: "ri-sun-line",
    href: "https://ironhavensupply.com/collections/off-grid-power-systems",
  },
];

function BrandBar() {
  return (
    <section className="py-14 bg-white border-t border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Available at Iron Haven Supply</p>
          <h2 className="text-2xl font-extrabold text-gray-900">Authorized Reseller for All Three Leading Brands</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {brands.map((b) => (
            <a
              key={b.name}
              href={b.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 p-5 rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50/30 transition-all cursor-pointer"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-xl shrink-0">
                <i className={`${b.icon} text-amber-500 text-xl`}></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-extrabold text-gray-900 text-base group-hover:text-amber-600 transition-colors"
                     dangerouslySetInnerHTML={{ __html: b.name }}
                  />
                  <span className="text-xs bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Authorized</span>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: b.claim }}
                />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SystemsComparisonPage() {
  return (
    <>
      {/* SEO Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Compare Backup Power Systems" }]} />
        </div>
      </div>

      <main>
        <ComparisonHero />
        <BrandBar />
        <MasterComparisonTable />
        <SystemDeepDives />
        <CostBreakdown />
        <UseCaseSelector />
        <ComparisonFAQ />
        <ComparisonCTA />
      </main>

      <Footer />
    </>
  );
}
